import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import { CATEGORIES, MAIN_SITE_URL } from "@/mocks/site";

export default function SiteHeader({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-200 bg-background-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link to={`/${lang}`} className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-lg font-extrabold text-foreground-950">usfanslink.lol</span>
            <span className="text-xs font-medium text-foreground-600">{t("brand.subtag")}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to={`/${lang}`}
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100 hover:text-primary-700"
          >
            <i className="ri-home-4-line mr-1.5" />
            {t("nav.home")}
          </Link>
          <Link
            to={`/${lang}/article/usfans-spreadsheet-guide`}
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100 hover:text-primary-700"
          >
            <i className="ri-shopping-bag-3-line mr-1.5" />
            {t("nav.guide")}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("cta.spreadsheet")}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 md:hidden"
          aria-label="Menu"
        >
          <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100dvh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to={`/${lang}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-home-4-line text-foreground-500" /> {t("nav.home")}
            </Link>
            <Link
              to={`/${lang}/article/usfans-spreadsheet-guide`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-shopping-bag-3-line text-foreground-500" /> {t("nav.guide")}
            </Link>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 rounded-md bg-primary-500 px-3 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t("cta.spreadsheet")}
            </a>
          </div>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("nav.browse")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {CATEGORIES.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  <span className="whitespace-nowrap">{t(`category.${cat.key}`)}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400" />
                </a>
              ))}
            </div>
          </div>

          <div className="mt-3 flex justify-end border-t border-background-200 pt-3">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
}